// src/components/admin/PayrollRules.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { policyAPI } from '../../services/api';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  PAYROLL RULES KEYS — must match seeds in seed_policy_configs.py           */
/* ─────────────────────────────────────────────────────────────────────────── */

const PAYROLL_KEYS = [
  'working_days_divisor',
  'late_days_per_deduction',
  'late_deduction_type',
  'overtime_eligible',
  'overtime_multiplier',
];

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Helpers                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

const fmtCur = (n) =>
  `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Simulate net pay given rules and a sample scenario. */
function simulateNetPay({ monthlySalary, workingDays, lateCount, unpaidLeaveDays, rules }) {
  const divisor         = parseInt(rules.working_days_divisor     || 30);
  const lateThreshold   = parseInt(rules.late_days_per_deduction  || 3);
  const deductionType   = rules.late_deduction_type || 'SALARY_DAY';
  const overtimeEnabled = (rules.overtime_eligible || 'false') === 'true';
  const overtimeMult    = parseFloat(rules.overtime_multiplier || 1.5);

  const dailySalary   = monthlySalary / divisor;
  const grossSalary   = dailySalary * workingDays;
  const leaveDeduct   = dailySalary * unpaidLeaveDays;
  const latePenalty   = Math.floor(lateCount / lateThreshold);   // units
  const lateDeduct    = deductionType === 'SALARY_DAY' ? dailySalary * latePenalty : 0;
  const elDeduct      = deductionType === 'EARNED_LEAVE' ? latePenalty : 0;   // days
  const netSalary     = Math.max(0, grossSalary - leaveDeduct - lateDeduct);

  return {
    dailySalary, grossSalary, leaveDeduct, latePenalty,
    lateDeduct, elDeduct, netSalary,
    deductionType, overtimeEnabled, overtimeMult,
  };
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Rule card                                                                  */
/* ─────────────────────────────────────────────────────────────────────────── */

function RuleCard({ config, value, onChange, isDirty, originalValue, onReset }) {
  const { value_type, choices, min_value, max_value } = config;

  const renderInput = () => {
    if (value_type === 'boolean') {
      const on = value === 'true' || value === true;
      return (
        <label style={S.toggleWrap}>
          <input
            type="checkbox"
            checked={on}
            onChange={e => onChange(e.target.checked ? 'true' : 'false')}
            style={{ display: 'none' }}
          />
          <span style={{ ...S.track, background: on ? '#4f46e5' : '#cbd5e1' }}>
            <span style={{ ...S.thumb, left: on ? 18 : 2 }} />
          </span>
          <span style={{ fontSize: 14, fontWeight: 600, color: on ? '#4f46e5' : '#94a3b8' }}>
            {on ? 'ON' : 'OFF'}
          </span>
        </label>
      );
    }

    if (value_type === 'choice' && Array.isArray(choices)) {
      return (
        <select value={value} onChange={e => onChange(e.target.value)} style={S.select}>
          {choices.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      );
    }

    const step = value_type === 'decimal' ? '0.1' : '1';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          style={S.adjBtn}
          onClick={() => {
            const n = parseFloat(value) - (value_type === 'decimal' ? 0.5 : 1);
            if (min_value === null || n >= min_value) onChange(String(n));
          }}
        >−</button>
        <input
          type="number"
          step={step}
          min={min_value ?? undefined}
          max={max_value ?? undefined}
          value={value}
          onChange={e => onChange(e.target.value)}
          style={S.numInput}
        />
        <button
          style={S.adjBtn}
          onClick={() => {
            const n = parseFloat(value) + (value_type === 'decimal' ? 0.5 : 1);
            if (max_value === null || n <= max_value) onChange(String(n));
          }}
        >+</button>
      </div>
    );
  };

  return (
    <div style={{ ...S.card, ...(isDirty ? S.cardDirty : {}) }}>
      <div style={S.cardTop}>
        <div>
          <div style={S.cardLabel}>
            {config.label}
            {isDirty && <span style={S.dotChanged} title="Unsaved">●</span>}
          </div>
          <div style={S.cardDesc}>{config.description}</div>
          <code style={S.keyCode}>{config.key}</code>
        </div>
        <div style={S.cardInput}>
          {renderInput()}
          {isDirty && (
            <div style={S.resetRow}>
              <span style={S.origLabel}>Previous: <strong>{originalValue}</strong></span>
              <button style={S.resetBtn} onClick={onReset}>Reset</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Live simulation panel                                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

function SimulationPanel({ rules }) {
  const [salary,        setSalary]        = useState(45000);
  const [workingDays,   setWorkingDays]   = useState(26);
  const [lateCount,     setLateCount]     = useState(4);
  const [unpaidLeave,   setUnpaidLeave]   = useState(1);

  const sim = simulateNetPay({
    monthlySalary:   salary,
    workingDays,
    lateCount,
    unpaidLeaveDays: unpaidLeave,
    rules,
  });

  const row = (label, amount, color) => (
    <div style={S.simRow}>
      <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: color || '#1a1d2b', fontFamily: 'monospace' }}>
        {fmtCur(amount)}
      </span>
    </div>
  );

  return (
    <div style={S.simPanel}>
      <div style={S.simTitle}>
        🔢 Live Salary Simulator
        <span style={S.simBadge}>Live preview — updates as you change rules</span>
      </div>

      {/* scenario inputs */}
      <div style={S.simInputGrid}>
        {[
          { label: 'Monthly Salary (₹)',   val: salary,      set: setSalary,      min: 0,  step: 1000 },
          { label: 'Working Days',         val: workingDays, set: setWorkingDays, min: 1,  step: 1,    max: 31 },
          { label: 'Late Arrivals',        val: lateCount,   set: setLateCount,   min: 0,  step: 1 },
          { label: 'Unpaid Leave Days',    val: unpaidLeave, set: setUnpaidLeave, min: 0,  step: 1 },
        ].map(({ label, val, set, min, step, max }) => (
          <label key={label} style={S.simInputLabel}>
            <span style={S.simInputLabelText}>{label}</span>
            <input
              type="number"
              value={val}
              min={min}
              max={max}
              step={step}
              onChange={e => set(parseFloat(e.target.value) || 0)}
              style={S.simInput}
            />
          </label>
        ))}
      </div>

      {/* results */}
      <div style={S.simResults}>
        {row(`Daily Rate (÷ ${rules.working_days_divisor || 30})`, sim.dailySalary, '#0284c7')}
        {row(`Period Gross (${workingDays} days × ${fmtCur(sim.dailySalary)})`, sim.grossSalary, '#059669')}
        <div style={S.simDivider} />
        {row(`Unpaid Leave Deduction (${unpaidLeave} days)`, sim.leaveDeduct, '#dc2626')}
        {row(
          `Late Penalty (${lateCount} lates ÷ ${rules.late_days_per_deduction || 3} = ${sim.latePenalty} day${sim.latePenalty !== 1 ? 's' : ''})`,
          sim.deductionType === 'SALARY_DAY' ? sim.lateDeduct : 0,
          '#dc2626',
        )}
        {sim.deductionType === 'EARNED_LEAVE' && sim.latePenalty > 0 && (
          <div style={S.simELNote}>
            ℹ {sim.latePenalty} Earned Leave day{sim.latePenalty !== 1 ? 's' : ''} will be deducted instead of salary.
          </div>
        )}
        <div style={S.simDivider} />
        <div style={{ ...S.simRow, marginTop: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Estimated Net Salary</span>
          <span style={{ fontSize: 18, fontWeight: 800, color: '#166534', fontFamily: 'monospace' }}>
            {fmtCur(sim.netSalary)}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main component                                                             */
/* ─────────────────────────────────────────────────────────────────────────── */

export default function PayrollRules() {
  const [allConfigs, setAllConfigs] = useState([]);
  const [dirty,      setDirty]      = useState({});
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [success,    setSuccess]    = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const data = await policyAPI.getAll('PAYROLL');
      setAllConfigs(Array.isArray(data) ? data : (data.results || []));
      setDirty({});
    } catch (e) {
      setError('Failed to load payroll rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const configs = allConfigs.filter(c => PAYROLL_KEYS.includes(c.key));

  /* merged rules object used by simulator */
  const liveRules = {};
  configs.forEach(c => {
    liveRules[c.key] = dirty[c.key] !== undefined ? dirty[c.key] : c.value;
  });

  const handleChange = (key, val) => setDirty(d => ({ ...d, [key]: val }));
  const handleReset  = (key)      => setDirty(d => { const n = { ...d }; delete n[key]; return n; });

  const hasDirty = Object.keys(dirty).length > 0;

  const handleSave = async () => {
    try {
      setSaving(true); setError(null);
      const updates = Object.entries(dirty).map(([key, value]) => ({ key, value }));
      await policyAPI.bulkUpdate(updates);
      setSuccess('Payroll rules saved successfully. New calculations will use these values immediately.');
      setTimeout(() => setSuccess(''), 5000);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save payroll rules.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 48 }}>
        <div style={S.spinner} /> <span style={{ color: '#64748b' }}>Loading payroll rules…</span>
      </div>
    );
  }

  return (
    <div>
      {error   && <div style={S.alertErr}>{error}<button style={S.alertClose} onClick={() => setError(null)}>×</button></div>}
      {success && <div style={S.alertOk}>✓ {success}<button style={S.alertClose} onClick={() => setSuccess('')}>×</button></div>}

      <div style={S.layout}>
        {/* ── left: rule cards ── */}
        <div style={S.rulesCol}>
          <div style={S.sectionTitle}>Business Rules</div>
          {configs.length === 0 ? (
            <div style={S.empty}>
              No payroll policies found. Run:&nbsp;
              <code>python manage.py seed_policy_configs</code>
            </div>
          ) : (
            configs.map(cfg => (
              <RuleCard
                key={cfg.key}
                config={cfg}
                value={dirty[cfg.key] !== undefined ? dirty[cfg.key] : cfg.value}
                onChange={val => handleChange(cfg.key, val)}
                isDirty={dirty[cfg.key] !== undefined}
                originalValue={cfg.value}
                onReset={() => handleReset(cfg.key)}
              />
            ))
          )}

          {/* save / discard */}
          {hasDirty && (
            <div style={S.actionBar}>
              <button style={S.btnDiscard} onClick={() => setDirty({})}>Discard Changes</button>
              <button style={S.btnSave}    onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : `💾 Apply ${Object.keys(dirty).length} Rule Change${Object.keys(dirty).length !== 1 ? 's' : ''}`}
              </button>
            </div>
          )}

          {/* audit note */}
          <div style={S.auditNote}>
            ⚠ Changes saved here take effect on the <strong>next payroll calculation</strong> run.
            Already-calculated payrolls are not retroactively changed.
          </div>
        </div>

        {/* ── right: simulator ── */}
        <div style={S.simCol}>
          <SimulationPanel rules={liveRules} />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Styles                                                                     */
/* ─────────────────────────────────────────────────────────────────────────── */

const S = {
  spinner: {
    width: 22, height: 22, border: '3px solid #e2e8f0', borderTopColor: '#4f46e5',
    borderRadius: '50%', animation: 'spin .7s linear infinite',
  },
  alertErr: {
    background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
    padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, position: 'relative',
  },
  alertOk: {
    background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
    padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, position: 'relative',
  },
  alertClose: {
    position: 'absolute', top: 8, right: 10, background: 'none', border: 'none',
    cursor: 'pointer', fontSize: 16, color: '#94a3b8',
  },
  layout: {
    display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap',
  },
  rulesCol: { flex: '1 1 400px', display: 'flex', flexDirection: 'column', gap: 12 },
  simCol:   { flex: '0 1 360px' },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 },
  empty: { padding: 24, color: '#94a3b8', fontSize: 13, textAlign: 'center', background: '#f8fafc', borderRadius: 8 },

  /* rule card */
  card: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
    padding: '16px 18px', transition: 'border-color .15s, background .15s',
  },
  cardDirty: { border: '1px solid #a78bfa', background: '#faf5ff' },
  cardTop:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  cardLabel: { fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 },
  dotChanged: { color: '#7c3aed', fontSize: 10 },
  cardDesc:  { fontSize: 12, color: '#64748b', lineHeight: 1.5, marginBottom: 6 },
  keyCode:   { fontSize: 11, background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: 5, fontFamily: 'monospace' },
  cardInput: { flex: '0 0 200px', display: 'flex', flexDirection: 'column', gap: 6 },

  /* toggle */
  toggleWrap: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  track: { width: 38, height: 22, borderRadius: 11, position: 'relative', display: 'inline-block', transition: 'background .2s', cursor: 'pointer', flexShrink: 0 },
  thumb: { position: 'absolute', width: 16, height: 16, borderRadius: '50%', background: '#fff', top: 3, transition: 'left .2s', boxShadow: '0 1px 3px #0003' },

  /* number input */
  adjBtn: {
    width: 30, height: 34, border: '1px solid #d1d5db', borderRadius: 8,
    background: '#f9fafb', cursor: 'pointer', fontSize: 16, fontWeight: 700,
    color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  numInput: {
    width: 80, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 15, fontFamily: 'monospace', textAlign: 'center', outline: 'none',
  },
  select: {
    padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 13, width: '100%', background: '#fff', cursor: 'pointer',
  },
  resetRow:    { display: 'flex', alignItems: 'center', gap: 6 },
  origLabel:   { fontSize: 11, color: '#94a3b8' },
  resetBtn:    { fontSize: 11, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 },

  /* actions */
  actionBar: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 },
  btnDiscard: { padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#374151' },
  btnSave:    { padding: '9px 22px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  auditNote:  { fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', padding: '10px 14px', borderRadius: 8, lineHeight: 1.6 },

  /* simulation panel */
  simPanel: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
    padding: 20, boxShadow: '0 2px 8px #0001', position: 'sticky', top: 20,
  },
  simTitle: { fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 },
  simBadge: { fontSize: 11, background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: 20, fontWeight: 600 },
  simInputGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 },
  simInputLabel: { display: 'flex', flexDirection: 'column', gap: 4 },
  simInputLabelText: { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' },
  simInput: {
    padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 14, fontFamily: 'monospace', outline: 'none',
  },
  simResults: { background: '#f8fafc', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
  simRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  simDivider: { borderTop: '1px dashed #e2e8f0', margin: '2px 0' },
  simELNote:  { fontSize: 11, color: '#6d28d9', background: '#ede9fe', padding: '6px 10px', borderRadius: 6 },
};